import { Icon } from '@scale/scaleui';
import { Skeleton } from '@mui/material';
import { ReactNode, useContext } from 'react';
import { CandidateMessageWithMetadata, ChatTaskContextType } from '@scale/shared/types/chat';
import { Card } from './Card';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';

type PromptCandidateProps = {
  candidate: CandidateMessageWithMetadata | string;
  onCandidateClick: () => void;
  errorButton?: ReactNode;
};

const PreSeededPromptCandidate = ({
  promptText,
  taskContext,
}: {
  promptText: string;
  taskContext: ChatTaskContextType;
}) => {
  return (
    <ChatMarkdown
      className="text-sm"
      markdown={promptText}
      enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin}
      enableSingleDollarTextMath={taskContext.milkdownEditorConfig?.enableSingleDollarTextMath}
      enableHtml={taskContext.htmlMarkdownConfig?.enablePromptPreviews}
    />
  );
};

const PromptCandidate = ({ candidate, onCandidateClick, errorButton }: PromptCandidateProps) => {
  const taskContext = useContext(ChatTaskContext);
  return (
    <Card
      className="h-full cursor-pointer shadow hover:border hover:border-primary-200 min-[800px]:flex-1 max-[800px]:w-96"
      onClick={onCandidateClick}
    >
      {typeof candidate === 'string' ? (
        <PreSeededPromptCandidate promptText={candidate} taskContext={taskContext} />
      ) : (
        <>
          {candidate.state === 'loading' && (
            <>
              <ChatMarkdown
                className="text-sm"
                markdown={candidate.message.content}
                enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin}
                enableSingleDollarTextMath={
                  taskContext.milkdownEditorConfig?.enableSingleDollarTextMath
                }
                enableHtml={taskContext.htmlMarkdownConfig?.enablePromptPreviews}
              />
              <Skeleton width={4} />
            </>
          )}
          {candidate.state === 'completed' && (
            <ChatMarkdown
              className="text-sm"
              markdown={candidate.message.content}
              enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin}
              enableSingleDollarTextMath={
                taskContext.milkdownEditorConfig?.enableSingleDollarTextMath
              }
              enableHtml={taskContext.htmlMarkdownConfig?.enablePromptPreviews}
            />
          )}
          {candidate.state === 'error' && (
            <div className="flex flex-col items-center gap-2 p-3">
              <div className="flex items-center text-danger-400">
                <Icon variant="danger" size="sm" icon="exclamation-circle" />
                Could not connect to the model.
              </div>
              {errorButton && <div>{errorButton}</div>}
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default PromptCandidate;
